import {
  useEffect,
  useMemo,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import styles from "./index.module.scss";
import {
  Form,
  Input,
  List,
  Selector,
  ImageUploader,
  Button,
  DatePicker,
  TextArea,
} from "antd-mobile";
import { useHistory } from "react-router-dom";
import AMap from "../../../components/AMapComp";
import useRescueAddressHook from "../common/hooks/useRescueAddressHook";
import { createOrderAction } from "../common/actions";
import { formatPhoneNumber } from "../common/js/util";
import { getServiceCarInfo } from "../common/js/rescueSdk";
import useQueryIndexDataHook from "../common/hooks/useQueryIndexDataHook";
import useQueryUserInfoHook from "../common/hooks/useQueryUserInfoHook";
import useBaseInfoHook from "../common/hooks/useBaseInfoHook";
import {
  RESCUE_REASONS,
  IF_TOWBAR,
  CAR_POSITIONS,
  Context,
  DEFAULT_SERVICE_CALL_NUMBER,
  RESCUE_REASON_SUB_OPTIONS,
  RESCUE_REASON_NAMES,
  RESCUE_TYPE_OPTIONS,
  RESCUE_TYPE,
  VEHICLE_DESCRIPTION_PLACEHOLDERS,
  RESCUE_REASON,
} from "../common/js/constant";
import useAMapHook from "../../../hooks/useAMapHook";
import { AddressSelectPopup } from "../components/AddressSelectPopup";
import FormCard from "../components/FormCard";
import dayjs from "dayjs";
import { getTowbarImage, getVehLocationAuth } from "../common/js/request";
import { upload2AliCloud } from "../common/js/util";
import { ExclamationCircleOutline } from "antd-mobile-icons";
import useTrailerAddress from "../common/hooks/useTrailerAddress";
import _ from "lodash";
import useUploadImages from "../common/hooks/useUploadImages";

const getSevenDayLater = (date: Date) => {
  const _date = new Date(date);
  _date.setDate(_date.getDate() + 7);
  return _date;
};

export default function RescueHome() {
  const history = useHistory();
  const [form] = Form.useForm();
  const { pageConfigData } = useQueryIndexDataHook();
  const { userInfo } = useQueryUserInfoHook();
  const { state, dispatch } = useContext(Context);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { rescueAddress, setRescueAddress } = useRescueAddressHook();
  const { baseInfo, setBaseInfo, updateBaseInfo } = useBaseInfoHook();
  const { trailerAddress, setTrailerAddress } = useTrailerAddress();
  const { uploadImages, setUploadImages } = useUploadImages();
  const mapWrapper = useRef(null);
  const { mapEntity, loaded, userCurrentPosition } = useAMapHook({
    container: mapWrapper.current,
  });

  const [vehLocationDone, setVehLocationDone] = useState(false);
  const [vehLocationResult, setVehLocationResult] = useState<{
    vehLocationLat: number;
    vehLocationLon: number;
  } | null>(null);
  const [addressPopupVisible, setAddressPopupVisible] = useState(false);
  const [rescueTimeSelectVisible, setRescueTimeSelectVisible] = useState(false);
  const currentTime = useRef(new Date());
  const sevenDayLater = useRef(getSevenDayLater(currentTime.current));
  const [towbarImage, setTowbarImage] = useState<string>("");
  // 判断是否显示救援原因子选项
  const showSubOptions = useMemo(() => {
    return (
      baseInfo.rescueReason && RESCUE_REASON_SUB_OPTIONS[baseInfo.rescueReason]
    );
  }, [baseInfo.rescueReason]);

  const showRescueTime = useMemo(() => {
    return baseInfo.rescueType === RESCUE_TYPE.LATER;
  }, [baseInfo.rescueType]);

  // 动态生成子选项标签
  const subOptionLabel = useMemo(() => {
    if (baseInfo.rescueReason && RESCUE_REASON_NAMES[baseInfo.rescueReason]) {
      return `${RESCUE_REASON_NAMES[baseInfo.rescueReason]}详情`;
    }
    return "救援原因详情";
  }, [baseInfo.rescueReason]);

  // 动态生成车辆状况描述占位符
  const rescueReasonRemarksPlaceholder = useMemo(() => {
    if (
      baseInfo.rescueReason &&
      VEHICLE_DESCRIPTION_PLACEHOLDERS[baseInfo.rescueReason]
    ) {
      return VEHICLE_DESCRIPTION_PLACEHOLDERS[baseInfo.rescueReason];
    }
    return "请描述车辆状况";
  }, [baseInfo.rescueReason]);

  useEffect(() => {
    document.title = "在线救援";
  }, []);

  useEffect(() => {
    let canceled = false;

    getServiceCarInfo()
      .then((result) => {
        if (canceled || !result.ok || !result.data) {
          setVehLocationDone(true);
          return;
        }
        const { plateNo, vin, vehicleId, carId, source } = result.data;
        if (!plateNo && !vin && !vehicleId && !carId) {
          setVehLocationDone(true);
          return;
        }

        setBaseInfo((prevBaseInfo) => {
          return {
            ...prevBaseInfo,
            plateNo: plateNo || prevBaseInfo.plateNo,
            vin: vin || prevBaseInfo.vin,
            vehicleId: vehicleId || prevBaseInfo.vehicleId,
            carId: carId || prevBaseInfo.carId,
            carInfoSource: source || "Service.getCarInfo",
          };
        });

        if (vin) {
          getVehLocationAuth({
            frontRedirect: window.location.href,
            vin,
          })
            .then((res) => {
              if (canceled) return;
              const { vehLocation, vehLocationLat, vehLocationLon } =
                res || {};

              if (vehLocation && vehLocationLat && vehLocationLon) {
                setVehLocationResult({ vehLocationLat, vehLocationLon });
              } else {
                setVehLocationDone(true);
              }
            })
            .catch(() => {
              if (!canceled) setVehLocationDone(true);
            });
        } else {
          setVehLocationDone(true);
        }
      })
      .catch(() => {
        setVehLocationDone(true);
      });
    return () => {
      canceled = true;
    };
  }, []);


  // 车辆定位成功后，等 AMap 加载完成再逆地理编码补全 adcode
  useEffect(() => {
    if (!vehLocationResult || !loaded) return;
    if (rescueAddress?.acdtAddress) {
      setVehLocationDone(true);
      return;
    }
    const { vehLocationLat, vehLocationLon } = vehLocationResult;

    window.AMap.plugin("AMap.Geocoder", () => {
      const geocoder = new window.AMap.Geocoder();
      geocoder.getAddress(
        [vehLocationLon, vehLocationLat],
        (status, result) => {
          if (status === "complete" && result.regeocode) {
            const regeo = result.regeocode;
            const addrComp = regeo.addressComponent;
            setRescueAddress((pre) => ({
              ...pre,
              acdtAddress: regeo.formattedAddress || "",
              acdtProvince: addrComp.province || "",
              provinceAdcode: addrComp.adcode?.substring(0, 2) + "0000" || "",
              acdtCity: addrComp.city || addrComp.province || "",
              cityAdcode: addrComp.citycode || addrComp.adcode?.substring(0, 2) + "0000" || "",
              acdtDistrict: addrComp.district || "",
              districtAdcode: addrComp.adcode || "",
              longitude: vehLocationLon,
              latitude: vehLocationLat,
            }));
          } else {
            setVehLocationDone(true);
          }
        }
      );
    });
  }, [vehLocationResult, loaded]);

  // 车管定位失败时，直接使用高德用户定位结果兜底填充待救援位置
  useEffect(() => {
    if (!vehLocationDone || rescueAddress?.acdtAddress) return;
    if (!userCurrentPosition?.position) return;
    const { lng, lat } = userCurrentPosition.position;
    if (!lng || !lat) return;
    const addrComp = userCurrentPosition.addressComponent || {};
    setRescueAddress((pre) => ({
      ...pre,
      acdtAddress: userCurrentPosition.formattedAddress || "",
      acdtProvince: addrComp.province || "",
      provinceAdcode: addrComp.adcode?.substring(0, 2) + "0000" || "",
      acdtCity: addrComp.city || addrComp.province || "",
      cityAdcode: addrComp.citycode || addrComp.adcode?.substring(0, 2) + "0000" || "",
      acdtDistrict: addrComp.district || "",
      districtAdcode: addrComp.adcode || "",
      longitude: lng,
      latitude: lat,
    }));
  }, [vehLocationDone, userCurrentPosition, rescueAddress?.acdtAddress]);

  useEffect(() => {
    // 订单创建成功则跳转至进度页
    if (state.createOrderState === 1) {
      setIsSubmiting(false);
      history.replace("/rescue-new/status");
    } else if (state.createOrderState < 0) {
      // 异常取消 loading
      setIsSubmiting(false);
    }
  }, [history, state.createOrderState]);

  // 自动填充用户信息
  useEffect(() => {
    if (!userInfo || !userInfo?.mobile) return;
    setBaseInfo((prevBaseInfo) => {
      const newUserInfo = {
        userName: prevBaseInfo?.userName || userInfo?.nickName || "",
        mobile: prevBaseInfo?.mobile || userInfo?.mobile || "",
        plateNo: prevBaseInfo?.plateNo || userInfo?.plateNo || "",
      };
      form.setFieldsValue(newUserInfo);
      return {
        ...prevBaseInfo,
        ...newUserInfo,
      };
    });
  }, [userInfo]);

  const searchTowbarImage = _.debounce((keywords) => {
    if (keywords && keywords.trim()) {
      getTowbarImage({ carLicenseNo: keywords })
        .then((res) => {
          const { towbarImageUrl } = res ?? {};
          setTowbarImage(towbarImageUrl || "");
        })
        .catch(() => {
          // 静默处理错误，不显示图片
          setTowbarImage("");
        });
    } else {
      // 车牌号为空时清除图片
      setTowbarImage("");
    }
  }, 300);

  // 监听车牌号变化，获取拖车钩图片
  useEffect(() => {
    searchTowbarImage(baseInfo.plateNo);
  }, [baseInfo.plateNo]);

  // 跳转到自动（手动）地位
  const handleLinkToAddress = () => {
    history.push("/rescue-new/address");
  };

  const isRescueReasonGroup1 = (reason) => {
    return (
      reason === RESCUE_REASON.TYRE_DEFECT ||
      reason === RESCUE_REASON.LOW_BATTERY
    );
  };

  const handleOpenAddressSelect = useCallback(() => {
    // 轮胎原因 或 车辆电量不足 - 跳转到拖车目的地页面
    if (isRescueReasonGroup1(baseInfo.rescueReason)) {
      history.push(
        "/rescue-new/trailer-destination?input=" + trailerAddress?.address,
      );
    }
    // 车辆故障 或 交通事故 - 打开搜索门店弹窗
    else {
      setAddressPopupVisible(true);
    }
  }, [baseInfo.rescueReason, trailerAddress?.address, history]);

  const handleSelectRescueTime = () => {
    setRescueTimeSelectVisible(true);
  };

  // 处理救援原因变化
  const handleRescueReasonChange = (value: string) => {
    const previousReason = baseInfo.rescueReason;
    let shouldResetDetail =
      !RESCUE_REASON_SUB_OPTIONS[value] || previousReason !== value;

    // 如果新选择的原因没有子选项，或者切换到了不同的救援原因，清除子选项值
    if (shouldResetDetail) {
      form.setFieldsValue({ rescueReasonDetail: undefined });
    }

    let shouldResetTrailerAddress = false;

    // 如果变更前后两个原因不在同一组则重置拖车目的地字段
    if (
      (isRescueReasonGroup1(previousReason) && !isRescueReasonGroup1(value)) ||
      (!isRescueReasonGroup1(previousReason) && isRescueReasonGroup1(value))
    ) {
      shouldResetTrailerAddress = true;
    }

    setBaseInfo((pre) => ({
      ...pre,
      rescueReason: value,
      rescueReasonDetail: shouldResetDetail ? "" : pre.rescueReasonDetail,
    }));

    if (shouldResetTrailerAddress) {
      setTrailerAddress({
        address: "",
        province: "",
        city: "",
        district: "",
        locationLon: 0,
        locationLat: 0,
      });
    }
  };

  // 处理救援原因子选项变化
  const handleRescueReasonDetailChange = (value: string) => {
    updateBaseInfo("rescueReasonDetail", value);
  };

  // 处理救援方式变化
  const handleRescueTypeChange = (value: string) => {
    setBaseInfo((pre) => ({
      ...pre,
      rescueType: value,
    }));
  };

  // 表单提交处理
  const onFinish = (values: any) => {
    if (isSubmiting) return;

    const fileArr = uploadImages.map((item) => ({
      url: item.url,
      fileName: item.extra?.fileName,
      fileId: item.key,
      category: "00", // 默认值
    }));

    const reqData = {
      ...values,
      ...baseInfo,
      ...rescueAddress,
      fileArr,
      rescueNumbers: 1, // 救援人数默认传1
      canShiftToN: "0", // 车辆状况默认无法挂N档/解除手刹
      trailerAddress,
    };

    // 后端类型问题，需要使用字符串拼接救援原因
    if (reqData.rescueReasonDetail) {
      reqData.rescueReason += `-${reqData.rescueReasonDetail}`;
    }

    // 救援类型和时间合并为一个字段
    reqData.rescueType = {
      time: reqData.rescueTime + ":00",
      type: reqData.rescueType,
    };

    // 选择立即救援时，去掉救援时间
    if (reqData.rescueType.type === RESCUE_TYPE.IMMEDIATE) {
      reqData.rescueType.time = undefined;
    }

    setIsSubmiting(true);
    dispatch(createOrderAction(reqData));
  };

  // 表单验证失败处理
  const onFinishFailed = (errorInfo: any) => {
    console.log("Validation failed:", errorInfo);
  };

  // 校验表单项
  const checkRequiredResult = useMemo(() => {
    if (!uploadImages?.length) {
      return false;
    }

    const reqireItemList = [
      "userName",
      "mobile",
      "plateNo",
      "acdtAddress",
      "rescueReason",
      "hasTowbar",
      "carPosition",
      "rescueReasonRemarks",
      "rescueType",
      "trailerAddress",
    ];

    // 如果显示子选项，则子选项也是必填的
    if (showSubOptions) {
      reqireItemList.push("rescueReasonDetail");
    }

    if (showRescueTime) {
      reqireItemList.push("rescueTime");
    }

    const combineValidateField = {
      ...baseInfo,
      ...rescueAddress,
      trailerAddress: trailerAddress?.address,
    };
    let isPass = true;
    reqireItemList.forEach((checkKey) => {
      const fieldValue = combineValidateField[checkKey];
      if (!fieldValue) {
        isPass = false;
      }
      if (fieldValue?.trim && !fieldValue.trim()) {
        isPass = false;
      }
    });
    return isPass;
  }, [
    baseInfo,
    uploadImages,
    rescueAddress,
    showRescueTime,
    showSubOptions,
    trailerAddress?.address,
  ]);

  //控制表单提交
  const handleSubmit = async () => {
    form.submit();
  };

  const labelRenderer = useCallback((type: string, data: number) => {
    switch (type) {
      case "year":
        return data + "年";
      case "month":
        return data + "月";
      case "day":
        return data + "日";
      case "hour":
        return data + "时";
      case "minute":
        return data + "分";
      case "second":
        return data + "秒";
      default:
        return data;
    }
  }, []);

  return (
    <div>
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          ...baseInfo,
          fileArr: uploadImages,
        }}
        className={styles.formContainer}
      >
        {/* 个人信息卡片 */}
        <FormCard title="个人信息">
          <Form.Item
            name="userName"
            label={<span className={styles.requiredLabel}>联系人姓名</span>}
            rules={[
              { required: true, message: "请输入正确的手机号码" },
              { pattern: /^[^\s]{1,20}$/, message: "请正确填写联系人姓名" },
            ]}
            layout="horizontal"
            className={styles.formItemHorizontal}
          >
            <Input
              placeholder="请填写"
              onChange={(val) => updateBaseInfo("userName", val)}
            />
          </Form.Item>
          <Form.Item
            name="mobile"
            label={<span className={styles.requiredLabel}>联系人电话</span>}
            rules={[
              { required: true, message: "请输入正确的手机号码" },
              { pattern: /^1[0-9]{10}$/, message: "请输入正确的手机号码" },
            ]}
            layout="horizontal"
            className={styles.formItemHorizontal}
          >
            <Input
              placeholder="请填写"
              onChange={(val) => updateBaseInfo("mobile", val)}
            />
          </Form.Item>
          <Form.Item
            name="plateNo"
            label={<span className={styles.requiredLabel}>车牌号码</span>}
            rules={[
              { required: true, message: "请填写车牌号" },
              { pattern: /.*[^\s]/, message: "请填写车牌号" },
            ]}
            layout="horizontal"
            className={styles.formItemHorizontal}
          >
            <Input
              placeholder="请填写"
              onChange={(val) => {
                updateBaseInfo("plateNo", val);
              }}
            />
          </Form.Item>
        </FormCard>

        {/* 待救援信息卡片 */}
        <FormCard title="待救援信息">
          <Form.Item
            name="acdtAddress"
            label={<span className={styles.requiredLabel}>待救援位置</span>}
            layout="horizontal"
            className={styles.formItemHorizontal}
          >
            <List.Item
              onClick={handleLinkToAddress}
              arrow={true}
              className={styles.choiceItem}
            >
              {rescueAddress?.acdtAddress || (
                <span className={styles.placeholder}>请选择</span>
              )}
            </List.Item>
          </Form.Item>

          <Form.Item
            name="rescueReason"
            label={<span className={styles.requiredLabel}>救援原因</span>}
            rules={[{ required: true, message: "请选择救援原因" }]}
            className={styles.formItemVertical}
          >
            <Selector
              options={RESCUE_REASONS.map((item) => ({
                label: item.name,
                value: item.value,
              }))}
              onChange={(arr) => handleRescueReasonChange(arr[0])}
              className={styles.selectorReason}
              multiple={false}
            />
          </Form.Item>

          {/* 救援原因子选项 */}
          {showSubOptions && baseInfo.rescueReason && (
            <Form.Item
              name="rescueReasonDetail"
              label={
                <span className={styles.requiredLabel}>{subOptionLabel}</span>
              }
              rules={[{ required: true, message: `请选择${subOptionLabel}` }]}
              className={styles.formItemVertical}
            >
              <Selector
                options={RESCUE_REASON_SUB_OPTIONS[baseInfo.rescueReason].map(
                  (item) => ({
                    label: item.name,
                    value: item.value,
                  }),
                )}
                onChange={(arr) =>
                  handleRescueReasonDetailChange(arr[0] as string)
                }
                className={styles.selectorReason}
                multiple={false}
              />
            </Form.Item>
          )}
        </FormCard>

        {/* 当前车辆状态卡片 */}
        <FormCard title="当前车辆状态">
          <Form.Item
            name="rescueReasonRemarks"
            label={<span className={styles.requiredLabel}>车辆状况描述</span>}
            rules={[{ required: true, message: "请描述车辆状况" }]}
            className={styles.formItemVertical}
          >
            <TextArea
              placeholder={rescueReasonRemarksPlaceholder}
              rows={3}
              maxLength={500}
              showCount={true}
              onChange={(val) => updateBaseInfo("rescueReasonRemarks", val)}
            />
          </Form.Item>

          <Form.Item
            name="hasTowbar"
            label={<span className={styles.requiredLabel}>拖车钩</span>}
            rules={[{ required: true, message: "请选择拖车钩" }]}
            className={styles.formItemVertical}
          >
            <Selector
              options={IF_TOWBAR.map((item) => ({
                label: item.name,
                value: item.value,
              }))}
              onChange={(arr) => updateBaseInfo("hasTowbar", arr[0])}
              className={styles.selectorTowbar}
            />
          </Form.Item>

          {/* 拖车钩图片显示 */}
          {towbarImage && (
            <div className={styles.towbarImageContainer}>
              <img
                src={towbarImage}
                alt="拖车钩位置示意图"
                className={styles.towbarImage}
              />
              <div className={styles.towbarImageHint}>示意图</div>
            </div>
          )}

          <Form.Item
            name="carPosition"
            label={<span className={styles.requiredLabel}>车辆停放位置</span>}
            rules={[{ required: true, message: "请选择车辆停放位置" }]}
            className={styles.formItemVertical}
          >
            <Selector
              options={CAR_POSITIONS.map((item) => ({
                label: item.name,
                value: item.value,
              }))}
              onChange={(arr) => updateBaseInfo("carPosition", arr[0])}
              className={styles.selectorParking}
            />
          </Form.Item>

          <Form.Item
            name="fileArr"
            label={<span className={styles.requiredLabel}>车辆现场照片</span>}
            rules={[
              {
                validator: (_, value) => {
                  if (!uploadImages || uploadImages.length === 0) {
                    return Promise.reject(new Error("请至少上传一张照片"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            className={styles.formItemVertical}
          >
            <ImageUploader
              value={uploadImages}
              onChange={(items) => {
                setUploadImages(items);
              }}
              upload={async (file) => {
                try {
                  const result: any = await upload2AliCloud(file);
                  return {
                    url: result.url,
                    key: result.fileId,
                    extra: {
                      fileName: result.fileName,
                    },
                  };
                } catch (error) {
                  console.error("Upload failed:", error);
                  throw error;
                }
              }}
              maxCount={3}
              multiple
              className={styles.imageUploader}
            />
          </Form.Item>
        </FormCard>

        {/* 救援方案卡片 */}
        <FormCard title="救援方案">
          {/* 警告提示 */}
          <div className={styles.warningBox}>
            <ExclamationCircleOutline className={styles.warningIcon} />
            <span className={styles.warningText}>
              仅限1人跟车，其他成员建议打车离开现场
            </span>
          </div>

          <Form.Item
            name="trailerAddress"
            label={<span className={styles.requiredLabel}>拖车目的地</span>}
            layout="horizontal"
            className={styles.formItemHorizontal}
          >
            <List.Item
              onClick={handleOpenAddressSelect}
              arrow={true}
              className={styles.choiceItem}
            >
              {trailerAddress.trailerShopName || trailerAddress.address ? (
                <div>
                  {trailerAddress.trailerShopName ?? trailerAddress.address}
                </div>
              ) : (
                <span className={styles.placeholder}>请选择</span>
              )}
            </List.Item>
          </Form.Item>

          <Form.Item
            name="rescueType"
            label={<span className={styles.requiredLabel}>救援方式</span>}
            className={styles.formItemVertical}
          >
            <Selector
              options={RESCUE_TYPE_OPTIONS.map((item) => ({
                label: item.name,
                value: item.value,
              }))}
              onChange={(arr) => handleRescueTypeChange(arr[0])}
              className={styles.selectorParking}
            />
          </Form.Item>
          {showRescueTime && (
            <Form.Item
              name="rescueTime"
              label={<span className={styles.requiredLabel}>救援时间</span>}
              layout="horizontal"
              className={styles.formItemHorizontal}
            >
              <List.Item
                onClick={handleSelectRescueTime}
                arrow={true}
                className={styles.choiceItem}
              >
                {baseInfo?.rescueTime || (
                  <span className={styles.placeholder}>请选择</span>
                )}
              </List.Item>
            </Form.Item>
          )}
        </FormCard>
      </Form>
      <div className={styles["fixed-button-field"]}>
        <Button
          block
          type="submit"
          color="primary"
          disabled={!checkRequiredResult || isSubmiting}
          loading={isSubmiting}
          onClick={handleSubmit}
          className={styles.submitButton}
        >
          {isSubmiting ? "正在提交..." : "申请救援"}
        </Button>
        {!!pageConfigData && (
          <div className={styles["rescue-hotline-field"]}>
            救援热线：
            <a
              className={styles["hotline-number"]}
              href={`tel:${pageConfigData.mobile || DEFAULT_SERVICE_CALL_NUMBER}`}
            >
              {formatPhoneNumber(
                pageConfigData.mobile || DEFAULT_SERVICE_CALL_NUMBER,
              )}
            </a>
          </div>
        )}
      </div>
      <div className={styles["hidden-map-wrapper"]}>
        <AMap loaded={loaded} mapEntity={mapEntity} mapWrapper={mapWrapper} />
      </div>
      {/* 拖车目的地 */}
      <AddressSelectPopup
        visible={addressPopupVisible}
        setVisible={setAddressPopupVisible}
        initValue={trailerAddress}
        position={{
          longitude: rescueAddress.longitude,
          latitude: rescueAddress.latitude,
        }}
        onSuccess={(v) => {
          // 处理门店或地址选择结果
          if (v.address) {
            setTrailerAddress(v);
          }
        }}
      />
      {/* 救援时间 */}
      <DatePicker
        visible={rescueTimeSelectVisible}
        onClose={() => {
          setRescueTimeSelectVisible(false);
        }}
        precision="minute"
        onConfirm={(v) => {
          updateBaseInfo("rescueTime", dayjs(v).format("YYYY-MM-DD HH:mm"));
        }}
        renderLabel={labelRenderer}
        min={currentTime.current}
        max={sevenDayLater.current}
      />
    </div>
  );
}
